import { chunk } from 'lodash-es';
import * as pdfMake from 'pdfmake-lite';

// Type definitions for pdfmake
interface TDocumentDefinitions {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  content: any[];
  pageSize?: string;
  pageOrientation?: string;
  pageMargins?: number[];
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  defaultStyle?: any;
}

interface TFontDictionary {
  [key: string]: {
    normal: string;
    bold: string;
    italics?: string;
    bolditalics?: string;
  };
}

// Interfaces for Avery labels
interface LabelLead {
  id: string;
  propertyInfo: {
    ownerInfo: {
      partyOwners?: Array<{
        fullName: string;
      }>;
    };
    streetNumber?: string;
    streetName?: string;
    streetSuffix?: string;
    streetPostDirection?: string;
    city?: string;
    state?: string;
    zipCode?: string;
  };
}

interface AveryLabelsConfig {
  title: string;
  leads: LabelLead[];
  action?: 'print' | 'download' | 'open';
  filename?: string;
}

/**
 * Generates Avery labels PDF object for better popup handling
 * Compatible with standard Avery 5160 labels (3 columns, 10 rows per page)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function generateAveryLabelsBlob(config: AveryLabelsConfig): any {
  const { leads } = config;

  // Chunk leads into rows of 3 (standard Avery label format)
  const chunkedLeads: Array<Array<LabelLead>> = chunk(leads, 3);

  // Build table content
  const content = chunkedLeads.map((leadsChunk: LabelLead[]) =>
    leadsChunk.map((lead: LabelLead, index: number) =>
      buildAveryLabelCell(lead, index < 2)
    )
  );

  // Handle incomplete last row - pdfmake requires complete rows
  const lastRow = content.pop();
  if (lastRow && lastRow.length < 3) {
    const cellsNeeded = 3 - lastRow.length;

    for (let index = 0; index < cellsNeeded; index++) {
      const hasMargin = index < 2;
      lastRow.push(buildAveryLabelCell(null, hasMargin, true));
    }
  }

  // Add the completed last row back
  if (lastRow) {
    content.push(lastRow);
  }

  const documentDefinition: TDocumentDefinitions = {
    content: [
      {
        layout: {
          defaultBorder: false,
          ...noPaddingLayout
        },
        table: {
          dontBreakRows: true,
          body: content,
        },
      },
    ],
    pageSize: 'LETTER',
    pageOrientation: 'portrait',
    // Avery 5160 margins in points (1 point = 1/72 inch)
    pageMargins: [13.5, 36, 9.825, 13.14],
    defaultStyle: {
      fontSize: 10,
      alignment: 'center',
    },
  };

  const fonts: TFontDictionary = getPDFMakeFontDefinition();
  const createdPDF = pdfMake.createPdf(documentDefinition, null, fonts);
  
  return createdPDF;
}

/**
 * Generates and processes Avery mailing labels PDF
 * Compatible with standard Avery 5160 labels (3 columns, 10 rows per page)
 */
export function generateAveryLabelsPDF(config: AveryLabelsConfig): void {
  const { title, leads, action = 'print', filename } = config;

  // Chunk leads into rows of 3 (standard Avery label format)
  const chunkedLeads: Array<Array<LabelLead>> = chunk(leads, 3);

  // Build table content
  const content = chunkedLeads.map((leadsChunk: LabelLead[]) =>
    leadsChunk.map((lead: LabelLead, index: number) =>
      buildAveryLabelCell(lead, index < 2)
    )
  );

  // Handle incomplete last row - pdfmake requires complete rows
  const lastRow = content.pop();
  if (lastRow && lastRow.length < 3) {
    const cellsNeeded = 3 - lastRow.length;

    for (let index = 0; index < cellsNeeded; index++) {
      const hasMargin = index < 2;
      lastRow.push(buildAveryLabelCell(null, hasMargin, true));
    }
  }

  // Add the completed last row back
  if (lastRow) {
    content.push(lastRow);
  }

  const documentDefinition: TDocumentDefinitions = {
    content: [
      {
        layout: {
          defaultBorder: false,
          ...noPaddingLayout
        },
        table: {
          dontBreakRows: true,
          body: content,
        },
      },
    ],
    pageSize: 'LETTER',
    pageOrientation: 'portrait',
    // Avery 5160 margins in points (1 point = 1/72 inch)
    pageMargins: [13.5, 36, 9.825, 13.14],
    defaultStyle: {
      fontSize: 10,
      alignment: 'center',
    },
  };

  processAveryLabelsExport(title, documentDefinition, action, filename);
}

/**
 * Builds individual label cell for Avery format
 */
function buildAveryLabelCell(
  lead: LabelLead | null,
  hasMargin: boolean,
  emptyState?: boolean
) {
  return {
    layout: {
      defaultBorder: false,
      ...noPaddingLayout
    },
    table: {
      heights: 72, // Standard Avery label height
      widths: [189.36], // Standard Avery label width
      body: [
        [
          {
            stack: [
              {
                text: emptyState ? '' : formatOwnerName(lead?.propertyInfo?.ownerInfo),
                bold: true,
                fontSize: 12,
                margin: [6, 3],
                characterSpacing: 0.3,
              },
              emptyState ? '' : formatAddressLine1(lead?.propertyInfo),
              emptyState ? '' : formatAddressLine2(lead?.propertyInfo),
            ],
          },
        ],
      ],
    },
    ...(hasMargin && { margin: [0, 0, 8.64, 0] }),
  };
}

/**
 * Formats owner name for label
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function formatOwnerName(ownerInfo: any): string {
  return ownerInfo?.partyOwners?.[0]?.fullName || '(name unavailable)';
}

/**
 * Formats address line 1 (street address)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function formatAddressLine1(propertyInfo: any): string {
  if (!propertyInfo) return '';

  const parts = [
    propertyInfo.streetNumber,
    propertyInfo.streetName,
    propertyInfo.streetSuffix,
    propertyInfo.streetPostDirection
  ].filter(Boolean);

  return parts.join(' ');
}

/**
 * Formats address line 2 (city, state, zip)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function formatAddressLine2(propertyInfo: any): string {
  if (!propertyInfo) return '';

  const cityStateZip = [
    propertyInfo.city,
    propertyInfo.state && propertyInfo.zipCode
      ? `${propertyInfo.state} ${propertyInfo.zipCode}`
      : propertyInfo.state || propertyInfo.zipCode
  ].filter(Boolean).join(', ');

  return cityStateZip;
}

/**
 * Processes the export request with popup blocking fallbacks
 */
function processAveryLabelsExport(
  title: string,
  documentDefinition: TDocumentDefinitions,
  action: 'print' | 'download' | 'open',
  filename?: string
): void {
  const currentDate = new Date().toLocaleDateString('en-US', {
    month: '2-digit',
    day: '2-digit',
    year: '2-digit'
  }).replace(/\//g, '-');

  const fonts: TFontDictionary = getPDFMakeFontDefinition();
  const createdPDF = pdfMake.createPdf(documentDefinition, null, fonts);

  switch (action) {
    case 'open':
      try {
        createdPDF.open();
      } catch (error) {
        console.warn('Failed to open PDF, trying download:', error);
        const fallbackFilename = filename || `Avery_Labels_${title}_${currentDate}`;
        createdPDF.download(fallbackFilename);
      }
      break;

    case 'download':
      const downloadFilename = filename || `Avery_Labels_${title}_${currentDate}`;
      createdPDF.download(downloadFilename);
      break;

    case 'print':
      try {
        // First try the print method
        createdPDF.print();
      } catch (printError) {
        console.warn('Print blocked, trying alternative methods:', printError);
        
        // Fallback 1: Try opening in new tab
        try {
          createdPDF.open();
        } catch (openError) {
          console.warn('Open blocked, offering download:', openError);
          
          // Fallback 2: Show user choice dialog
          const userChoice = window.confirm(
            'Popup blocked by browser. Would you like to download the Avery labels PDF instead?'
          );
          
          if (userChoice) {
            const fallbackFilename = filename || `Avery_Labels_${title}_${currentDate}`;
            createdPDF.download(fallbackFilename);
          }
        }
      }
      break;

    default:
      // Default to download as safest option
      const defaultFilename = filename || `Avery_Labels_${title}_${currentDate}`;
      createdPDF.download(defaultFilename);
      break;
  }
}

/**
 * PDF font definition for web fonts
 */
function getPDFMakeFontDefinition(): TFontDictionary {
  return {
    Roboto: {
      normal: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Regular.ttf',
      bold: 'https://cdnjs.cloudflare.com/ajax/libs/pdfmake/0.1.66/fonts/Roboto/Roboto-Medium.ttf',
    },
  };
}

/**
 * Layout configuration for no padding/borders
 */
const noPaddingLayout = {
  paddingLeft: () => 0,
  paddingRight: () => 0,
  paddingTop: () => 0,
  paddingBottom: () => 0,
  vLineWidth: () => 0,
  hLineWidth: () => 0,
};
